// src/pages/Game.tsx - Enhanced and Corrected

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ddApi } from '../lib/supabaseClient';
import { Game, Card, GameMove, TSOProfile, GameState, ActiveEffect } from '../types';
import CardDisplay from '../components/CardDisplay';
import { useAuth } from '../context/AuthContext';
import '../styles/GameInterface.css';

// Helper components for Icons. For a real app, you'd move these to their own files.
// --- START: Icon Components ---

const FactionIcon: React.FC<{ faction: 'attacker' | 'defender', className?: string }> = ({ faction, className }) => {
  if (faction === 'attacker') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M20.283 2.283L21.717 3.717L12 13.434L2.283 3.717L3.717 2.283L12 10.566L20.283 2.283ZM10.566 12L2.283 20.283L3.717 21.717L12 13.434L20.283 21.717L21.717 20.283L13.434 12L21.717 3.717" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 2ZM12 11.99H19C18.47 16.11 15.72 19.78 12 20.92V12H5V6.3L12 4.15V11.99Z" />
    </svg>
  );
};

const LogIcon: React.FC<{ type: 'play' | 'damage' | 'defense', className?: string }> = ({ type, className }) => {
  switch (type) {
    case 'damage': // Swords
      return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M11.68 4.32L12.41 5.05L13.13 4.32L14.55 5.74L13.82 6.47L18.73 11.38L19.44 10.67L20.86 12.08L12.08 20.86L10.67 19.44L11.38 18.73L6.47 13.82L5.74 14.55L4.32 13.13L5.05 12.41L4.32 11.68L3.14 12.86V20H5.14V16.86L6.59 18.31L7.29 17.6L12.21 22.5L17.12 17.6L17.83 18.31L19.28 16.86V20H21.28V12.86L11.68 4.32Z" /></svg>;
    case 'defense': // Shield
      return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M12 2L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 2Z" /></svg>;
    case 'play': // Card icon
    default:
      return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z" /></svg>;
  }
};

// --- END: Icon Components ---


// --- Log Entry Type Definition ---
type FormattedLogEntry = {
  key: string;
  turn: number;
  playerName: string;
  isPlayerMove: boolean;
  cardName: string;
  damage: number;
  defense: number;
};

const GameInterface: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [loading, setLoading] = useState(true);
  const [playingCard, setPlayingCard] = useState(false);
  const [gameLog, setGameLog] = useState<FormattedLogEntry[]>([]);
  const [opponentProfile, setOpponentProfile] = useState<TSOProfile | null>(null);
  const [activeEffects, setActiveEffects] = useState<ActiveEffect[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load game state on component mount
  useEffect(() => {
    if (!gameId || !user) return;
    loadGameState();
  }, [gameId, user]);

  const loadGameState = async () => {
    if (!gameId || !user) return;
    try {
      setLoading(true);
      setError(null);
      const game = await ddApi.getGame(parseInt(gameId), user.id);
      if (!game) {
        navigate('/dashboard');
        return;
      }
      const isPlayer1 = game.player1_id === user.id;
      const deckChoice = isPlayer1 ? game.player1_deck_choice : game.player2_deck_choice;
      const [playerCards, gameHistory, effects] = await Promise.all([
        ddApi.getGameCards(user.id, deckChoice),
        ddApi.getGameMoves(parseInt(gameId)),
        ddApi.getActiveEffects(parseInt(gameId))
      ]);
      const isMyTurn = (game.current_turn === 1 && isPlayer1) || (game.current_turn === 2 && !isPlayer1);
      const opponentId = isPlayer1 ? game.player2_id : game.player1_id;
      const opponents = await ddApi.getAvailablePlayers(user.id);
      const opponent = opponents.find(p => p.user_id === opponentId);
      setOpponentProfile(opponent || null);

      const state: GameState = {
        game, playerCards, gameHistory: gameHistory || [], activeEffects: effects || [],
        isMyTurn, isPlayer1, playerDefense: isPlayer1 ? (game.player1_defense || 0) : (game.player2_defense || 0)
      };
      setGameState(state);
      setActiveEffects(effects || []);
      generateGameLog(state, opponent || null);
    } catch (error) {
      console.error('Error loading game:', error);
      setError('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const generateGameLog = (state: GameState, opponent: TSOProfile | null) => {
    const log: FormattedLogEntry[] = state.gameHistory.map((move, index) => {
      const isPlayerMove = move.player_id === user!.id;
      return {
        key: `move-${index}`,
        turn: move.turn_number,
        playerName: isPlayerMove ? 'You' : (opponent?.username || 'Opponent'),
        isPlayerMove,
        cardName: move.card?.name || 'Unknown Card',
        damage: move.damage_dealt,
        defense: move.defense_consumed,
      };
    });
    setGameLog(log.reverse()); // Show newest first
  };

  const getHealthPercentage = (health: number) => (health / 20) * 100;

  const getPlayerInfo = () => {
    if (!gameState) return { playerHealth: 20, opponentHealth: 20 };
    return {
      playerHealth: gameState.isPlayer1 ? gameState.game.player1_health : gameState.game.player2_health,
      opponentHealth: gameState.isPlayer1 ? gameState.game.player2_health : gameState.game.player1_health
    };
  };

  const canPlayCard = (card: Card): { canPlay: boolean; reason?: string } => {
    if (!gameState || !gameState.isMyTurn || gameState.game.game_status !== 'active') return { canPlay: false, reason: 'Not your turn' };
    const { playerHealth } = getPlayerInfo();
    if (card.name === 'Desperate Strike' && playerHealth > 8) return { canPlay: false, reason: 'Health must be 8 or less' };
    if (['Zero Day', 'Logic Bomb', 'AI Sentinel'].includes(card.name)) {
      const hasUsed = activeEffects.some(effect => effect.player_id === user!.id && effect.effect_type === 'card_used_once' && effect.effect_data.card_id === card.id);
      if (hasUsed) return { canPlay: false, reason: 'Can only be used once' };
    }
    return { canPlay: true };
  };

  const playCard = async (card: Card) => {
    if (!gameState || !gameState.isMyTurn || playingCard || !gameId) return;
    const validation = canPlayCard(card);
    if (!validation.canPlay) {
      setError(validation.reason || 'Cannot play this card');
      return;
    }
    try {
      setPlayingCard(true);
      setError(null);
      const result = await ddApi.playCard(parseInt(gameId), user!.id, card.id);
      if (!result.success) {
        setError(result.error || 'Failed to play card');
        return;
      }
      await loadGameState();
      setSelectedCard(null);
    } catch (error) {
      setError('Network error - please try again');
    } finally {
      setPlayingCard(false);
    }
  };

  const selectCard = (card: Card) => {
    if (!gameState?.isMyTurn || gameState.game.game_status !== 'active') return;
    const validation = canPlayCard(card);
    if (!validation.canPlay) {
      setError(validation.reason || 'Cannot select this card');
      return;
    }
    setSelectedCard(card);
    setError(null);
  };

  const getPlayerActiveEffects = () => { if (!user) return []; return activeEffects.filter(effect => effect.player_id === user.id); };
  const getOpponentActiveEffects = () => { if (!user) return []; return activeEffects.filter(effect => effect.player_id !== user.id); };
const formatEffectName = (effectType: string, effectData?: any) => {
  switch (effectType) {
    case 'damage_reduction': return '🛡️ Shield';
    case 'block_attack': return '🚫 Block';
    case 'defense_bonus': return '⬆️ +Def';
    case 'card_used_once': return '✓ Used';
    case 'social_engineering_immunity': return '🔒 SAAT';
    case 'air_gap_isolation': return '🔐 Air Gap';
    case 'honeypot_counter': return '🍯 Trap';
    case 'apt_persistent_damage': 
      const turnsLeft = effectData?.turns_remaining || 0;
      return `🔥 APT(${turnsLeft})`;
    case 'bios_rootkit_damage': return '💀 Rootkit';
    case 'sleeper_agent_dormant':
      const dormancyLeft = effectData?.turns_remaining || 0;
      return `😴 Agent(${dormancyLeft})`;
    case 'sleeper_agent_active': return '⚡ Agent+';
    case 'network_jitter_next_turn': return '📡 Jitter';
    case 'swap_next_card_values': return '🔄 Swap';
    case 'nullify_next_attack': return '❌ Nullify';
    case 'ransomware_attack_reduction':
      const reduction = effectData?.reduction_amount || 1;
      return `🦠 Ransom(-${reduction})`;
    case 'ai_sentinel_used_in_game': return '🤖 Sentinel';
    case 'soc_counter_attack':
      const uses = effectData?.uses_remaining || 0;
      return `⚔️ SOC(${uses})`;
    case 'reveal_opponent_defense': return '👁️ Reveal';
    case 'redundant_infrastructure_backup': return '🔄 Backup';
    default: return '✨ Effect';
  }
};  

const PlayerStatusDisplay: React.FC<{
  effects: ActiveEffect[];
  defense: number;
  playerName: string;
}> = ({ effects, defense, playerName }) => {
  const visibleEffects = effects.filter(effect => 
    effect.effect_type !== 'card_used_once' && 
    effect.effect_type !== 'ai_sentinel_used_in_game'
  );

  if (defense === 0 && visibleEffects.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {defense > 0 && (
        <div className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">
          🛡️ {defense}
        </div>
      )}
      
      {visibleEffects.slice(0, 4).map((effect) => (
        <div key={effect.id} className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-bold">
          {formatEffectName(effect.effect_type, effect.effect_data)}
        </div>
      ))}
      
      {visibleEffects.length > 4 && (
        <div className="bg-gray-600 text-white px-2 py-1 rounded text-xs font-bold">
          +{visibleEffects.length - 4}
        </div>
      )}
    </div>
  );
};


  if (loading || !gameState) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white text-xl">
        Loading game...
      </div>
    );
  }

  const { playerHealth, opponentHealth } = getPlayerInfo();
  const isGameOver = gameState.game.game_status === 'completed';
  const didIWin = gameState.game.winner_id === user!.id;

  return (
    <>
      <style>{`
        body { background-color: #111827; }
        .glass-panel {
          background: rgba(31, 41, 55, 0.25);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .neon-glow-blue { filter: drop-shadow(0 0 4px #3b82f6) drop-shadow(0 0 8px #3b82f6); }
        .neon-glow-red { filter: drop-shadow(0 0 4px #ef4444) drop-shadow(0 0 8px #ef4444); }
        .holographic-card {
          background: linear-gradient(45deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1));
          border: 1px solid rgba(255, 255, 255, 0.2);
          position: relative;
          overflow: hidden;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .holographic-card:hover {
          transform: translateY(-10px) scale(1.05);
          box-shadow: 0 0 15px rgba(139, 92, 246, 0.6);
        }
        .holographic-card.selected {
          transform: translateY(-10px) scale(1.05);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
          border-color: #3b82f6;
        }
        .health-bar-glow {
            box-shadow: 0 0 6px 1px var(--glow-color);
        }
        .vs-divider-glow {
            text-shadow: 0 0 8px #fff, 0 0 12px #3b82f6, 0 0 16px #ef4444;
        }
      `}</style>

      <div className="min-h-screen text-white font-sans">
        {/* --- HEADER RESTORED --- */}
        <header className="bg-gray-800/50 border-b border-gray-700 p-4 sticky top-0 z-10 glass-panel">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div>
                <h1 className="text-2xl font-bold flex items-baseline text-blue-300">
                Digital Duel
                </h1>
                <p className="text-gray-400">
                Game #{gameState.game.game_id} • Turn {gameState.game.turn_number}
                </p>
            </div>
            <button
                onClick={() => navigate('/dashboard')}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors"
            >
                Back to Dashboard
            </button>
            </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-600/50 glass-panel p-4 rounded-lg mb-6 border-red-500">
              <p>{error}</p>
              <button onClick={() => setError(null)} className="mt-2 bg-red-700/80 hover:bg-red-600 px-3 py-1 rounded text-sm">
                Dismiss
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">

              {/* --- Player Panels --- */}
              <div className="glass-panel rounded-lg p-4 space-y-4">
                {/* Opponent Panel */}
                <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${!gameState.isMyTurn ? 'bg-red-500/30' : 'bg-gray-700/30'}`}>
                        <FactionIcon faction="attacker" className={`w-10 h-10 text-red-400 ${!gameState.isMyTurn ? 'neon-glow-red' : ''}`} />
                    </div>
 <div className="flex-1">
  <div className="font-bold text-lg">{opponentProfile?.username || 'Opponent'}</div>
  <div className="text-sm text-gray-400">
    🛡️ Defense: {
      gameState.game.opponentDefenseRevealed 
        ? (gameState.isPlayer1 ? gameState.game.player2_defense : gameState.game.player1_defense)
        : 'Hidden'
    }
    {gameState.game.opponentDefenseRevealed && (
      <span className="text-yellow-400 ml-2">👁️ REVEALED</span>
    )}
  </div>
  
  {/* ADD THIS LINE: */}
  <PlayerStatusDisplay
    effects={activeEffects.filter(e => e.player_id !== user!.id)}
    defense={gameState.game.opponentDefenseRevealed 
      ? (gameState.isPlayer1 ? (gameState.game.player2_defense || 0) : (gameState.game.player1_defense || 0))
      : 0
    }
    playerName="Opponent"
  />
</div>
					
                    <div className="w-1/3">
                        <div className="h-4 bg-gray-700 rounded-full">
                            <div className="h-4 bg-red-500 rounded-full health-bar-glow" style={{ width: `${getHealthPercentage(opponentHealth)}%`, '--glow-color': '#ef4444' } as React.CSSProperties}></div>
                        </div>
                        <div className="text-right text-sm font-semibold mt-1">{opponentHealth}/20</div>
                    </div>
                </div>

                {/* VS Divider */}
                <div className="text-center text-4xl font-black text-gray-400 vs-divider-glow">VS</div>

                {/* Player Panel */}
                <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${gameState.isMyTurn ? 'bg-blue-500/30' : 'bg-gray-700/30'}`}>
                        <FactionIcon faction="defender" className={`w-10 h-10 text-blue-400 ${gameState.isMyTurn ? 'neon-glow-blue' : ''}`} />
                    </div>
<div className="flex-1">
  <div className="font-bold text-lg">{profile?.username}</div>
  <div className="text-sm text-gray-300">🛡️ Defense: {gameState.playerDefense}</div>
  
  {/* ADD THIS LINE: */}
  <PlayerStatusDisplay
    effects={activeEffects.filter(e => e.player_id === user!.id)}
    defense={gameState.playerDefense}
    playerName="You"
  />
</div>
                    <div className="w-1/3">
                        <div className="h-4 bg-gray-700 rounded-full">
                            <div className="h-4 bg-blue-500 rounded-full health-bar-glow" style={{ width: `${getHealthPercentage(playerHealth)}%`, '--glow-color': '#3b82f6' } as React.CSSProperties}></div>
                        </div>
                        <div className="text-right text-sm font-semibold mt-1">{playerHealth}/20</div>
                    </div>
                </div>
              </div>



              {/* Last Card Played - Moved above player cards */}
              {!isGameOver && gameState.gameHistory.length > 0 && (
                <div className="glass-panel rounded-lg p-6">
                  <h3 className="text-center text-lg font-semibold text-gray-300 mb-4">Last Card Played</h3>
                  
                  {(() => {
                    const lastMove = gameState.gameHistory[gameState.gameHistory.length - 1];
                    const lastCard = lastMove?.card;
                    
                    if (!lastCard) return null;
                    
                    const wasMyMove = lastMove.player_id === user!.id;
                    const playerName = wasMyMove ? 'You' : (opponentProfile?.username || 'Opponent');
                    
                               return (
                    <div className="last-card-display-enhanced">
                      {/* Card Info Panel */}
                      <div className="card-info-panel-enhanced">
                        <div className="card-player-enhanced">
                          <span className={wasMyMove ? 'text-blue-400' : 'text-red-400'}>
                            {playerName}
                          </span>
                          <span className="text-gray-300 mx-1">played</span>
                        </div>
                        <div className="card-name-enhanced">
                          {lastMove.card?.name}
                        </div>
                        {(lastMove.damage_dealt > 0 || lastMove.defense_consumed > 0) && (
                          <div className="card-effects-enhanced">
                            {lastMove.damage_dealt > 0 && (
                              <div className="effect-damage-enhanced">
                                ⚔️ {lastMove.damage_dealt} damage
                              </div>
                            )}
                            {lastMove.defense_consumed > 0 && (
                              <div className="effect-defense-enhanced">
                                🛡️ {lastMove.defense_consumed} defense consumed
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Card Preview */}
                      <div className="card-preview-enhanced w-48">
                        <CardDisplay 
                          card={lastCard}
                          showDetails={true}
                          disabled={true}
                        />
                      </div>
                    </div>
                  );
                  })()}
                </div>
              )}

              {/* --- Game Status / Player Hand --- */}
              <div className="glass-panel rounded-lg p-6">
                <div className="text-center mb-6">
                  {isGameOver ? (
                    <h2 className={`text-3xl font-bold ${didIWin ? 'text-green-400 neon-glow-blue' : 'text-red-400 neon-glow-red'}`}>
                      {didIWin ? '🎉 VICTORY' : '💀 DEFEAT'}
                    </h2>
                  ) : gameState.isMyTurn ? (
                    <h2 className="text-2xl font-bold text-blue-300 neon-glow-blue">🎯 YOUR TURN</h2>
                  ) : (
                    <h2 className="text-2xl font-bold text-gray-400">⏳ WAITING FOR OPPONENT</h2>
                  )}
                </div>

                {gameState.isMyTurn && !isGameOver && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {gameState.playerCards.map(card => {
                        const validation = canPlayCard(card);
                        return (
                          <div key={card.id} className={`relative holographic-card rounded-lg ${selectedCard?.id === card.id ? 'selected' : ''}`}
                               onClick={() => validation.canPlay && selectCard(card)}>
                            <CardDisplay card={card} selected={selectedCard?.id === card.id} disabled={!validation.canPlay} showDetails={false} />
                            {!validation.canPlay && (
                              <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center p-2 text-center">
                                <span className="text-red-400 font-semibold">{validation.reason}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {selectedCard && (
                        <div className="mt-6 text-center">
                            {/* Play button moved to sidebar - this can be removed or kept as backup */}
                        </div>
                    )}
                  </>
                )}
                
                {isGameOver && (
                    <div className="text-center mt-6">
                        <button onClick={() => navigate('/dashboard')} className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-3 rounded-lg font-medium">
                            Return to Dashboard
                        </button>
                    </div>
                )}
              </div>
            </div>


            {/* --- Enhanced Sidebar --- */}
            <div className="sidebar-enhanced flex flex-col space-y-6">
              
              {/* Enhanced Game Log */}
              <div className="sidebar-panel-enhanced rounded-lg p-4">
                <h3 className="panel-title-enhanced">📋 Game Log</h3>
                <div className="game-log-enhanced">
                  {gameLog.length === 0 ? (
                    <div className="text-sm text-gray-400">No moves yet...</div>
                  ) : (
                    gameLog.map((log, index) => (
                      <div key={log.key} className={`flex items-start gap-3 text-sm p-2 rounded-md ${log.isPlayerMove ? 'bg-blue-500/10' : 'bg-red-500/10'}`}>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-300">
                          {log.turn}
                        </div>
                        <LogIcon type={log.damage > 0 ? 'damage' : log.defense > 0 ? 'defense' : 'play'} className={`w-5 h-5 mt-0.5 flex-shrink-0 ${log.isPlayerMove ? 'text-blue-400' : 'text-red-400'}`} />
                        <div className="flex-1">
                          <p>
                            <span className={`font-bold ${log.isPlayerMove ? 'text-blue-300' : 'text-red-300'}`}>{log.playerName}</span> played <span className="font-semibold text-white">{log.cardName}</span>
                          </p>
                          {(log.damage > 0 || log.defense > 0) && (
                            <p className="text-xs text-gray-400">
                              {log.damage > 0 && `⚔️ ${log.damage} dmg`}{log.damage > 0 && log.defense > 0 && ' ' }
                              {log.defense > 0 && `🛡️ ${log.defense} def consumed`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Selected Card Preview with Play Button */}
              {selectedCard && (
                 <div className="sidebar-panel-enhanced rounded-lg p-4">
                    <h4 className="panel-title-enhanced">🎯 Selected Card</h4>
                    <CardDisplay card={selectedCard} showDetails disabled={false} />
                    
                    {/* Play Card Button in Sidebar */}
                    <div className="mt-4">
                      <button
                        onClick={() => playCard(selectedCard)}
                        disabled={playingCard}
                        className="w-full px-4 py-3 rounded-lg font-bold text-lg transition-all bg-blue-600/50 hover:bg-blue-500 hover:shadow-lg hover:shadow-blue-500/50 border border-blue-400"
                      >
                        {playingCard ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Playing...
                          </span>
                        ) : (
                          `Play ${selectedCard.name}`
                        )}
                      </button>
                    </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default GameInterface;